import { useEffect, useState } from 'react';
import { Button } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { ConfirmationModal } from '../../../Atoms/ConfirmationModal';
import { ComponentName } from '../detailsDrawersHelpers';
import { SupplierMap } from '../../../../store/SupplierMap';
import { ApMap } from '../../../../store/ApMap';
import { getUserSubject, User, UserRole } from '../../../../store/User';
import { Link } from '../../../../store/Link';
import { clearDrawerState } from '../../../../store/Drawer';
import { ProductMap } from '../../../../store/ProductMap';
import { CustomerMap } from '../../../../store/CustomerMap';
import { RunTotalAudit } from '../../../../store/RunTotalAudit';
import { ComponentSpinner } from '../../Loading/ComponentSpinner';
import { PaymentRecipient } from '../../../../store/PaymentRecipient';
import { Payment } from '../../../../store/Payment';
import { Correction } from '../../../../store/Correction';

export interface DrawerActionsProps {
  componentName?: ComponentName;
  data:
    | User
    | SupplierMap
    | Link
    | RunTotalAudit
    | ApMap
    | ProductMap
    | CustomerMap
    | Payment
    | PaymentRecipient
    | Correction;
  deleteAction?: (id: string) => void;
}

const DELETE_NAMES: Record<string, string> = {
  [ComponentName.PAYMENT]: 'Payment',
  [ComponentName.PAYMENT_RECIPIENT]: 'Payment Recipient',
  [ComponentName.LINK]: 'Link',
  [ComponentName.CORRECTION]: 'Correction'
};

export const DrawerActions = ({ componentName, data, deleteAction }: DrawerActionsProps) => {
  const [user, setUser] = useState<User>();
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const userSub = getUserSubject().subscribe((u) => setUser(u));

    return () => {
      if (userSub) userSub.unsubscribe();
    };
  }, []);

  const [showConfirm, setShowConfirm] = useState(false);

  const handleDelete = async () => {
    const primaryKey = componentName === ComponentName.CUSTOMER_MAP ? 'customerId' : 'id';
    if (deleteAction && data && data[primaryKey as keyof typeof data]) {
      const id = data[primaryKey as keyof typeof data];
      setIsLoading(true);
      await deleteAction(id);
      clearDrawerState();
      setIsLoading(false);
    }
  };

  const defaultButtons = () => {
    if (isLoading) {
      return <ComponentSpinner label="Deleting..." />;
    }

    return (
      <>
        <Button
          data-testid="drawer-delete-button"
          color="error"
          onClick={() => setShowConfirm(true)}
          aria-label="delete"
          startIcon={<DeleteIcon color="buttonGrey" />}>
          Delete
        </Button>
        <ConfirmationModal
          open={showConfirm}
          setOpen={setShowConfirm}
          handleSubmit={handleDelete}
          text={`Are you sure you want to delete this ${DELETE_NAMES[componentName || ''] || 'item'}?`}
          confirmButtonText={'Delete'}
          title={'Delete Confirmation'}
        />
      </>
    );
  };

  const userRole = user?.role as UserRole;
  const isUserAdminOrEngineer = [UserRole.ADMIN, UserRole.ENGINEER].includes(userRole);
  const isUserAdminOrEngineerOrAnalyst = isUserAdminOrEngineer || userRole === UserRole.ANALYST;
  const showActions = () => {
    if (!componentName || !deleteAction) return false;
    return (
      [
        ComponentName.SUPPLIER_MAP,
        ComponentName.PRODUCT_MAP,
        ComponentName.AP_MAP,
        ComponentName.CUSTOMER_MAP
      ].includes(componentName) ||
      ([ComponentName.LINK, ComponentName.PAYMENT, ComponentName.PAYMENT_RECIPIENT].includes(componentName) &&
        isUserAdminOrEngineer) ||
      (componentName === ComponentName.CORRECTION && isUserAdminOrEngineerOrAnalyst)
    );
  };

  const getActions = () => {
    if (showActions()) {
      return <div className="flex items-center mb-2">{defaultButtons()}</div>;
    } else {
      return <></>;
    }
  };

  return getActions();
};
